"use client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/lib/api";
import { bidQuery, type BidReply, type BidSchemas } from "@/lib/api/bids";
import { getOperationByKey, type CommandOptions } from "@/lib/api/suppliers";
import { createCommandKey } from "@/lib/commands/command-key";
import {
  bidCompletion,
  bidIntent,
  type BidOperation,
  type BidOperationType,
  type BidScope,
} from "@/lib/commands/bid-operation-store";
import { eventBindingKey } from "@/lib/commands/event-operation-store";
import { eventAuthorityLost } from "@/lib/commands/event-feedback";
import { bidFeedback, type BidFeedback } from "@/lib/commands/bid-feedback";
import {
  invalidateEvent,
  useEventInvalidationBridge,
} from "@/lib/commands/event-invalidation";
import {
  useEventRuntime,
  type EventRuntimeConfig,
} from "@/components/events/event-runtime";
import { useBidRecovery } from "./use-bid-recovery";

type Outcome =
  { ok: true; result: BidReply } | { ok: false; feedback: BidFeedback };
type Send = (options: CommandOptions) => Promise<{ data: BidReply }>;
interface Runtime {
  records: BidOperation[];
  ready: boolean;
  available: boolean;
  pending: boolean;
  lookupFailed: boolean;
  terminal?: "Completed" | "Failed" | "FormerGrant";
  restore: () => void;
  dismiss: () => void;
  blocked: (scope: BidScope) => boolean;
  completion: (scope: BidScope) => string;
  run: (
    scope: BidScope,
    type: BidOperationType,
    send: Send,
  ) => Promise<Outcome>;
  lookup: (record: BidOperation) => Promise<void>;
}
const Context = createContext<Runtime | null>(null);
export function BidRuntimeProvider({
  config,
  children,
}: {
  config: EventRuntimeConfig;
  children: ReactNode;
}) {
  const event = useEventRuntime();
  const recovery = useBidRecovery(config.binding);
  const latest = useRef({ config, event });
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const [pending, setPending] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [terminal, setTerminal] = useState<Runtime["terminal"]>();
  const [completed, setCompleted] = useState<Record<string, string>>({});
  useEventInvalidationBridge();
  useEffect(() => {
    latest.current = { config, event };
  }, [config, event]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);
  const alive = (scope: BidScope, signal: AbortSignal) =>
    mounted.current &&
    !signal.aborted &&
    !latest.current.event.isDenied(scope.eventId);
  function blocked(scope: BidScope) {
    return (
      !recovery.available ||
      pending ||
      event.isDenied(scope.eventId) ||
      recovery.records.some((record) => bidIntent(record) === bidIntent(scope))
    );
  }
  function deny(scope: BidScope, error: unknown) {
    try {
      recovery
        .read()
        .filter((row) => row.eventId === scope.eventId)
        .forEach((row) => recovery.remove(row.idempotencyKey));
    } catch {}
    latest.current.event.deny(scope.eventId, error);
  }
  function finish(record: BidOperation, recovered: boolean) {
    if (!recovery.remove(record.idempotencyKey)) return false;
    if (recovered) {
      setTerminal("Completed");
      setCompleted((before) => ({
        ...before,
        [bidIntent(record)]: record.idempotencyKey,
      }));
    }
    invalidateEvent(record.eventId);
    return true;
  }
  async function perform(
    scope: BidScope,
    type: BidOperationType,
    send: Send,
  ): Promise<Outcome> {
    const signal = controller.current!.signal;
    const record: BidOperation = {
      ...scope,
      operationType: type,
      idempotencyKey: createCommandKey("bid"),
      startedAt: Date.now(),
    };
    try {
      if (recovery.read().some((row) => bidIntent(row) === bidIntent(scope)))
        return { ok: false, feedback: "blocked" };
      if (!(await latest.current.config.verify(signal)))
        return { ok: false, feedback: "forbidden" };
      if (!alive(scope, signal)) return { ok: false, feedback: "unknown" };
      if (!recovery.save(record)) return { ok: false, feedback: "storage" };
      const { data } = await send({
        tenantId: config.binding.tenantId,
        csrfToken: latest.current.config.csrfToken,
        idempotencyKey: record.idempotencyKey,
        signal,
      });
      if (!alive(scope, signal) || !bidCompletion(data, record))
        return { ok: false, feedback: "unknown" };
      return finish(record, false)
        ? { ok: true, result: data }
        : { ok: false, feedback: "storage" };
    } catch (error) {
      if (!alive(scope, signal)) return { ok: false, feedback: "unknown" };
      const feedback = bidFeedback(error);
      if (feedback !== "unknown") recovery.remove(record.idempotencyKey);
      if (eventAuthorityLost(error)) deny(scope, error);
      else if (
        [
          "version_conflict",
          "event_closed",
          "event_revision_changed",
          "bid_window_changed",
        ].includes(feedback)
      )
        invalidateEvent(scope.eventId);
      return { ok: false, feedback };
    }
  }
  async function run(
    scope: BidScope,
    type: BidOperationType,
    send: Send,
  ): Promise<Outcome> {
    if (controller.current || blocked(scope))
      return { ok: false, feedback: "blocked" };
    if (!navigator.locks) return { ok: false, feedback: "storage" };
    controller.current = new AbortController();
    setPending(true);
    try {
      return await navigator.locks.request(
        eventBindingKey(config.binding),
        { ifAvailable: true },
        (lock) =>
          lock
            ? perform(scope, type, send)
            : Promise.resolve({
                ok: false as const,
                feedback: "blocked" as const,
              }),
      );
    } catch {
      return { ok: false, feedback: "storage" };
    } finally {
      controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  async function lookup(record: BidOperation) {
    if (controller.current || event.isDenied(record.eventId)) return;
    controller.current = new AbortController();
    const signal = controller.current.signal;
    setPending(true);
    setLookupFailed(false);
    try {
      if (!(await latest.current.config.verify(signal))) return;
      const params = new URLSearchParams({
        grantId: record.grantId ?? "",
        operationType: record.operationType,
        key: record.idempotencyKey,
      });
      const response =
        config.binding.audience === "supplier"
          ? await bidQuery<BidSchemas["BidOperationResponse"]>(
              config.binding,
              record.eventId,
              `/operations/by-key?${params}`,
              signal,
            )
          : await getOperationByKey(
              { tenantId: config.binding.tenantId, signal },
              record.operationType,
              record.idempotencyKey,
            );
      if (!alive(record, signal)) return;
      if (response.data.state === "Failed") {
        if (recovery.remove(record.idempotencyKey)) setTerminal("Failed");
        return;
      }
      if (
        response.data.state !== "Completed" ||
        !bidCompletion(response.data.result, record)
      ) {
        setLookupFailed(true);
        return;
      }
      finish(record, true);
    } catch (error) {
      if (!alive(record, signal)) return;
      if (
        error instanceof ApiError &&
        error.code === "bid_operation_grant_unavailable"
      ) {
        if (recovery.remove(record.idempotencyKey)) {
          setTerminal("FormerGrant");
          invalidateEvent(record.eventId);
        }
      } else if (eventAuthorityLost(error)) deny(record, error);
      else setLookupFailed(true);
    } finally {
      controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        records: recovery.records,
        ready: recovery.ready,
        available: recovery.available,
        pending,
        lookupFailed,
        terminal,
        dismiss: () => setTerminal(undefined),
        restore: recovery.sync,
        completion: (scope) => completed[bidIntent(scope)] ?? "initial",
        blocked,
        run,
        lookup,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useBidRuntime() {
  const value = useContext(Context);
  if (!value) throw new Error("Bid runtime is unavailable");
  return value;
}
